import { Box, UtilButton, Card, MaterialSymbol, chooseColor, formattedCurrency } from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { format, addDays, parse, parseISO } from 'date-fns';
import { useBank } from '@/providers/Bank.provider';
import styles from './Welcome.module.scss';

import { useLayout } from '@/providers/Layout.provider';
import { RejectSetupDialogueHandler, DialogueHandlerProps } from './types';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';
import RejectSetupContext, { RejectSetupCtx } from './Dialogue/RejectSetupContext';
import RejectSetupDialogue from './Dialogue/RejectSetupDialogue';
import usePostPaylinkDecline from '@/hooks/useApi/usePostPaylinkDecline';
import usePostPaylinkSetAccount from '@/hooks/useApi/usePostPaylinkSetAccount';
import { BrandingLogo, Title, Logos, Button, LanguageSwitcher } from 'UI';

const Welcome: FunctionComponent = () => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Welcome' });
  const { flow, paylink, Token } = usePaylink();
  const { setLayoutInfo } = useLayout();
  const [showRejectSetupConfirmationAlert, setshowRejectSetupConfirmationAlert] = useState(false);
  const [declinePaylinkResponse, declinePaylink] = usePostPaylinkDecline({});
  const [setPaylinkAccountResponse, setPaylinkAccount] = usePostPaylinkSetAccount({});
  const [paymentSelectionMethod, setPaymentSelectionMethod] = useState('');
  const { resetPaymentMethod } = useFlexiblePayment();
  const { setBankAccountRail } = useBank();
  const { token } = useToken();
  const RejectSetup: RejectSetupCtx = {
    showRejectSetupConfirmationAlert,
  };

  useEffect(() => {
    const paymentMethods = ['bank', 'credit', 'debitcard', 'email', 'googlepay', 'applepay', 'paypal', 'venmo'];
    const selectedMethods = paymentMethods.filter((method) => String(token?.PaymentSelectionMethod).includes(method));

    if (selectedMethods.length > 0) {
      setPaymentSelectionMethod(selectedMethods.join(','));
    } else if (String(token?.PaymentSelectionMethod) === 'any') {
      setPaymentSelectionMethod('any');
    } else {
      setPaymentSelectionMethod('any');
    }
  }, [token]);

  useEffect(() => {
    resetPaymentMethod();
  }, []);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<RejectSetupDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: RejectSetupDialogueHandler = {
        reject: {
          yes: {
            default: () => {
              declinePaylink({ SingleUseToken: Token });
            },
          },
          no: {
            default: () => {
              setshowRejectSetupConfirmationAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };
  useEffect(() => {
    if (declinePaylinkResponse.Success) {
      history.push('/error');
      setshowRejectSetupConfirmationAlert(false);
    }
  }, [declinePaylinkResponse]);
  useEffect(() => {
    if (setPaylinkAccountResponse && setPaylinkAccountResponse.Success) {
      history.push('/thank-you');
    } else if (setPaylinkAccountResponse && !setPaylinkAccountResponse.Success) {
      history.push('/connect/transaction-error');
    }
  }, [setPaylinkAccountResponse]);

  useEffect(() => {
    setLayoutInfo(
      chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      80,
      20,
      0,
      false,
      false,
    );
  }, []);

  const continueNextStep = () => {
    if (paylink.FlexiblePaymentOptions) {
      const flexiblePaymentOptions = paylink.FlexiblePaymentOptions.split(',');
      if (flexiblePaymentOptions.length === 1) {
        const Option = flexiblePaymentOptions[0];
        if (Option === 'multiple') {
          history.push('/connect/flexible-payment/multiple-payment-methods');
        } else if (Option === 'partial') {
          history.push('/connect/flexible-payment/partial-payment');
        } else if (Option === 'installments') {
          history.push('/connect/flexible-payment/installment-plan');
        }
      }
      history.push('/connect/flexible-payment');
      return;
    }

    if (paylink.BankAccountLinked) {
      setPaylinkAccount({ SingleUseToken: Token, Iq11Token: paylink.BankAccountToken || '', PaymentMethod: 'bank' });
    } else if (paylink.PaymentMethodDisplayOptions !== 'New') {
      history.push('/otp');
    } else {
      const shouldGoToConnect = paymentSelectionMethod === 'bank' && paylink.BankAccountRail.length > 1;

      if (shouldGoToConnect) {
        history.push({
          pathname: '/connect',
          state: { paymentMethod: paymentSelectionMethod, origin: 'token' },
        });
      } else {
        switch (paymentSelectionMethod) {
          case 'bank':
            setBankAccountRail(paylink.BankAccountRail[0]);
            history.push('/connect/bank');
            break;
          case 'credit':
            history.push({
              pathname: '/connect/processor/credit',
              state: { origin: 'token' },
            });
            break;
          case 'debitcard':
            history.push({
              pathname: '/connect/processor/debitcard',
              state: { origin: 'token' },
            });
            break;
          case 'email':
            history.push({
              pathname: '/connect/processor/email',
              state: { origin: 'token' },
            });
            break;
          case 'paypal':
            history.push({
              pathname: '/connect/digital-wallet/paypal',
              state: { origin: 'token' },
            });
            break;
          case 'venmo':
            history.push({
              pathname: '/connect/digital-wallet/venmo',
              state: { origin: 'token' },
            });
            break;
          default:
            history.push({
              pathname: '/connect',
              state: { paymentMethod: paymentSelectionMethod, origin: 'token' },
            });
        }
      }
    }
  };

  const isWithdrawFlow = flow === Flow.WITHDRAW__MULTIPLE_PAYMENTS || flow === Flow.WITHDRAW__SINGLE;
  const isFundFlow = flow === Flow.FUND__SCHEDULED || flow === Flow.FUND__SINGLE;
  const isConnectFlow = flow === Flow.CONNECT;
  const expireDate = useMemo(() => {
    if (!paylink.TransactionDate || !paylink.ExpireDate) return '';

    return format(
      addDays(parse(paylink.TransactionDate, 'yyyy-MM-dd HH:mm:ss', new Date()), Number(paylink.ExpireDate)),
      'MMM do, yyyy',
    );
  }, [paylink.TransactionDate, paylink.ExpireDate]);

  const getTitle = () => {
    const frequency = paylink.NameOfFrequency
      ? t(`Frequency.${paylink.NameOfFrequency}`, { defaultValue: paylink.NameOfFrequency })
      : undefined;
    const endDate = paylink.ScheduleEndDate ? format(parseISO(paylink.ScheduleEndDate), 'MMM do, yyyy') : undefined;

    if (flow === Flow.WITHDRAW__SINGLE) {
      return t('payTitle', {
        senderName: paylink.SenderName,
        currency: paylink.Currency,
        amount: formattedCurrency(paylink.Amount),
        defaultValue: '{{senderName}} sent {{currency}} {{amount}}',
      });
    }

    if (flow === Flow.WITHDRAW__MULTIPLE_PAYMENTS) {
      if (paylink.ScheduleEndDate && paylink.Frequency) {
        return t('recurringPayTitleWithDate', {
          senderName: paylink.SenderName,
          currency: paylink.Currency,
          amount: formattedCurrency(paylink.Amount),
          frequency,
          endDate,
          defaultValue:
            '{{senderName}} sent a Recurring Payment of {{currency}} {{amount}} {{frequency}} until {{endDate}}',
        });
      }
      if (paylink.EndingAfterPayments && paylink.Frequency) {
        return t('recurringPayTitleWithEnding', {
          senderName: paylink.SenderName,
          currency: paylink.Currency,
          amount: formattedCurrency(paylink.Amount),
          frequency,
          ending: paylink.EndingAfterPayments,
          defaultValue:
            '{{senderName}} sent a Recurring Payment of {{currency}} {{amount}} {{frequency}} until {{ending}} payments',
        });
      }
    }

    if (flow === Flow.FUND__SINGLE) {
      return t('collectTitle', {
        senderName: paylink.SenderName,
        currency: paylink.Currency,
        amount: formattedCurrency(paylink.Amount),
        defaultValue: '{{senderName}} requested {{currency}} {{amount}}',
      });
    }

    if (flow === Flow.FUND__SCHEDULED) {
      if (paylink.ScheduleEndDate && paylink.Frequency) {
        return t('recurringCollectTitle', {
          senderName: paylink.SenderName,
          currency: paylink.Currency,
          amount: formattedCurrency(paylink.Amount),
          frequency,
          endDate,
          defaultValue:
            '{{senderName}} requested a Recurring Payment of {{currency}} {{amount}} {{frequency}} until {{endDate}}',
        });
      }
      if (paylink.EndingAfterPayments && paylink.Frequency) {
        return t('recurringCollectTitle', {
          senderName: paylink.SenderName,
          currency: paylink.Currency,
          amount: formattedCurrency(paylink.Amount),
          frequency,
          ending: paylink.EndingAfterPayments,
          defaultValue:
            '{{senderName}} requested a Recurring Payment of {{currency}} {{amount}} {{frequency}} until {{ending}} payments',
        });
      }
    }

    if (isConnectFlow) {
      return t('connectTitle', {
        senderName: paylink.SenderName,
        defaultValue: '{{senderName}} sent you a payment method connection request.',
      });
    }

    return '';
  };

  return (
    <RejectSetupContext.Provider value={RejectSetup}>
      <div className={styles.wrapper}>
        <Card className={styles.card} hoverEffect={false}>
          <Box jc="sb" ai="center">
            <div>
              {token?.BrandingDetails?.Logo && (
                <BrandingLogo className={styles.logo} src={token?.BrandingDetails?.Logo} />
              )}
            </div>
            <LanguageSwitcher />
          </Box>

          <Box flx="col" gapSize="24px">
            <Box flx="col" gapSize="4px">
              <Title level="2">{getTitle()}</Title>
              {paylink.DocumentLink ? (
                <UtilButton
                  iconLeft="open_in_new"
                  onClick={() => {
                    window.open(paylink.DocumentLink, '_blank');
                  }}
                >
                  {paylink.InvoiceNumber
                    ? t('viewButton', { defaultValue: 'View' })
                    : t('viewInvoiceButton', { defaultValue: 'View Invoice' })}
                  {paylink.InvoiceNumber && ` #${paylink.InvoiceNumber}`}
                </UtilButton>
              ) : (
                paylink.InvoiceNumber && <div className={styles.invoice}>#{paylink.InvoiceNumber}</div>
              )}
            </Box>

            {paylink.RecipientMessage && (
              <div className={styles.note}>
                <Box height="auto" gapSize="8px" pb="sm">
                  <MaterialSymbol icon="sticky_note_2" />
                  <Title className={styles.noteTitle} level="6">
                    {(isConnectFlow || isWithdrawFlow) && t('senderNote', { defaultValue: 'Sender’s Note' })}

                    {isFundFlow && t('receiverNote', { defaultValue: 'Receiver’s Note' })}
                  </Title>
                </Box>
                <p className={styles.description}>{paylink.RecipientMessage}</p>
              </div>
            )}
            <div className={styles.description}>
              {isConnectFlow &&
                t('connectDescription', {
                  defaultValue:
                    'In order to fulfill this request, you will need to connect your preferred payment method.',
                })}

              {!isConnectFlow && (
                <>
                  <Trans
                    t={t}
                    i18nKey="transactionDescription"
                    values={{ endDate: expireDate }}
                    components={{
                      font: <span style={{ color: 'var(--vopayBlack)' }} />,
                    }}
                    defaults="This request must be completed by <font>{{endDate}}</font>"
                  />{' '}
                  {paylink.PaymentMethodDisplayOptions === 'New'
                    ? t('transactionDescriptionNew', {
                        defaultValue:
                          'In order to fulfill this request, you will need to connect your preferred payment method.',
                      })
                    : paylink.PaymentMethodDisplayOptions === 'Existing'
                      ? t('transactionDescriptionExisting', {
                          defaultValue: 'In order to fulfill this request, you can select an existing payment method',
                        })
                      : t('transactionDescriptionBoth', {
                          defaultValue:
                            'In order to fulfill this request, you can connect a new payment method or select an existing one.',
                        })}
                </>
              )}
            </div>
          </Box>

          <div className={styles.buttonContainer}>
            {paylink.CanDecline === '1' && (
              <Button size="small" onClick={() => setshowRejectSetupConfirmationAlert(true)}>
                {t('declineButton', { defaultValue: 'Decline' })}
              </Button>
            )}
            <Button size="small" colorScheme="primary" onClick={() => continueNextStep()}>
              {t('continueButton', { defaultValue: 'Continue' })}
            </Button>
          </div>
        </Card>
        <div className={styles.brandsContainer}>
          <Logos />
        </div>
      </div>

      <RejectSetupDialogue handleDialogue={handlers.handleDialogue} />
    </RejectSetupContext.Provider>
  );
};

export default Welcome;
